import type { Point2, ViewportState } from '@frontend/viewer-core';

import { SNAP_TOLERANCE_PX } from '../constants';
import type { DrawingModel, PointerState, ToolId } from './types';

type Dependencies = {
  viewport: {
    getState(): ViewportState;
    screenToWorld(screen: Point2): Point2;
  };
  getModel: () => DrawingModel | null;
  getTool: () => ToolId;
  getHiddenLayers: () => ReadonlySet<number>;
};

/** Resolves a screen point to a world point, snapped when the Measure tool is active. */
export type SnapResolver = (screenPoint: Point2) => PointerState;

/**
 * Builds the resolver shared by the live pointer overlay and the Measure tool.
 *
 * Both need the exact same answer to "where does this screen point land, and
 * onto what" — the overlay to draw a snap indicator, Measure to commit the
 * point it draws. A second implementation of the tolerance or the fallback
 * order would eventually drift from this one.
 */
export const createSnapResolver = ({
  viewport,
  getModel,
  getTool,
  getHiddenLayers,
}: Dependencies): SnapResolver => {
  /**
   * Resolves a screen point to a world point, moved onto a snap target when one
   * is near. The tolerance is a screen distance converted through the current
   * zoom, so snapping feels the same at every magnification.
   */
  return (screenPoint) => {
    const world = viewport.screenToWorld(screenPoint);
    const model = getModel();
    if (!model) {
      return { screen: screenPoint, world, snap: null };
    }

    // Snapping is a Measure affordance. Skipping it elsewhere also keeps two
    // spatial queries off every pointer move under the Select tool.
    if (getTool() !== 'measure') {
      return { screen: screenPoint, world, snap: null };
    }

    const { scale } = viewport.getState();
    const tolerance = SNAP_TOLERANCE_PX / (scale || 1);
    const hiddenLayers = getHiddenLayers();

    // Distinctive targets first — a corner, a centre, a midpoint. These are the
    // points a person is actually reaching for, and they must not be beaten by a
    // spot on a line that happens to be a fraction closer.
    const discrete = model.snapIndex.nearest(world, tolerance, hiddenLayers);
    if (discrete) {
      return { screen: screenPoint, world: discrete.point, snap: discrete };
    }

    // Otherwise snap anywhere along the nearest line, so a measurement can start
    // part-way down an edge rather than only at the features either side of it.
    const onEdge = model.index.nearestPointOn(world, {
      tolerance,
      hiddenLayers,
    });
    if (onEdge) {
      return {
        screen: screenPoint,
        world: onEdge.point,
        snap: { point: onEdge.point, kind: 'edge', layer: onEdge.layer },
      };
    }

    return { screen: screenPoint, world, snap: null };
  };
};
